import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../hooks/useApi";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle, Clock, Copy } from "lucide-react";

const STATUS_META = {
  verified: { icon: CheckCircle2, style: "text-foreground border-foreground" },
  invalid: { icon: AlertTriangle, style: "text-foreground border-foreground border-dashed" },
  error: { icon: XCircle, style: "text-foreground border-foreground" },
  missing: { icon: Clock, style: "text-foreground/70 border-foreground/35" },
};

function StatusBadge({ status, extra, testId }) {
  const meta = STATUS_META[status] || STATUS_META.missing;
  const Icon = meta.icon;
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wide border px-3 py-1 ${meta.style}`}>
      <Icon className="w-3 h-3" /> {status} {extra}
    </span>
  );
}

function money(cents, interval) {
  const amt = (cents / 100).toFixed(2);
  if (!interval) return `$${amt} one-time`;
  return `$${amt}/${interval}`;
}

function ConnectionCard({ data, onValidate, validating }) {
  const status = data?.status === "verified" ? "verified" : data?.status === "error" ? "error" : "missing";
  return (
    <div className="bg-card border border-border p-6 mb-8" data-testid="billing-connection-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-subtitle">Stripe Connection</h2>
        <button data-testid="billing-connection-validate-btn" onClick={onValidate} disabled={validating}
          className="text-xs uppercase tracking-wide text-foreground underline underline-offset-2 hover:opacity-70 disabled:opacity-40">
          {validating ? "Checking…" : "Re-check"}
        </button>
      </div>
      <StatusBadge status={status} extra={data?.environment ? `· ${data.environment} mode` : ""} testId="billing-connection-status-badge" />
      <div className="text-sm text-foreground/60 space-y-1 mt-3">
        {data?.account_id && <p>Account: <span className="text-foreground/90">{data.account_id}</span></p>}
        {data?.business_name && <p>Business: <span className="text-foreground/90">{data.business_name}</span></p>}
        {data?.country && <p>Country: <span className="text-foreground/90">{data.country}</span></p>}
        {data?.note && <p className="text-foreground/70">{data.note}</p>}
        {data?.error && <p className="text-foreground font-medium">{data.error}</p>}
      </div>
    </div>
  );
}

function OfferRow({ offer, onSave, onValidate, onSetGate }) {
  const [priceId, setPriceId] = useState(offer.stripe_price_id || "");
  const [busy, setBusy] = useState(false);
  const [confirmingEnable, setConfirmingEnable] = useState(false);

  const save = async () => {
    if (!priceId.trim()) return toast.error("Enter a Stripe Price ID first.");
    setBusy(true);
    try {
      const saved = await onSave(offer.offer_key, priceId.trim());
      if (saved?.verification_status === "verified") {
        toast.success(`${offer.label} mapping saved and verified`);
      } else {
        toast.error(`${offer.label} mapping saved but failed verification — see errors below`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save mapping");
    } finally {
      setBusy(false);
    }
  };

  const revalidate = async () => {
    setBusy(true);
    try {
      await onValidate(offer.offer_key);
      toast.success("Re-validated against Stripe");
    } catch {
      toast.error("Validation failed");
    } finally {
      setBusy(false);
    }
  };

  const setGate = async (enabled) => {
    setBusy(true);
    try {
      await onSetGate(offer.offer_key, enabled);
      toast.success(enabled ? `${offer.label} checkout turned ON` : `${offer.label} checkout turned OFF`);
      setConfirmingEnable(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change checkout gate");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border p-5 mb-4" data-testid={`billing-offer-card-${offer.offer_key}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
        <div>
          <p className="text-sm font-medium">{offer.label}</p>
          <p className="text-xs text-foreground/70 uppercase tracking-wide">{money(offer.approved_amount_cents, offer.approved_interval)} · {offer.billing_type.replace("_", " ")}</p>
        </div>
        <StatusBadge status={offer.verification_status} testId={`billing-offer-status-${offer.offer_key}`} />
      </div>
      <div className="flex items-center gap-2 mb-2">
        <input
          data-testid={`billing-offer-price-input-${offer.offer_key}`}
          value={priceId}
          onChange={(e) => setPriceId(e.target.value)}
          placeholder="price_..."
          className="bg-background border border-border flex-1 px-3 py-2 text-sm font-mono"
        />
        <button data-testid={`billing-offer-save-btn-${offer.offer_key}`} onClick={save} disabled={busy}
          className="bg-foreground text-background px-4 py-2 text-xs uppercase tracking-wide disabled:opacity-40">
          Save &amp; Verify
        </button>
        {offer.stripe_price_id && (
          <button data-testid={`billing-offer-revalidate-btn-${offer.offer_key}`} onClick={revalidate} disabled={busy}
            className="text-xs uppercase tracking-wide text-foreground/75 hover:text-foreground px-3 py-2">
            Re-validate
          </button>
        )}
      </div>
      {offer.verification_errors?.length > 0 && (
        <ul className="text-xs text-foreground/70 space-y-1 mb-2" data-testid={`billing-offer-errors-${offer.offer_key}`}>
          {offer.verification_errors.map((e, i) => <li key={i} className="flex gap-1.5"><AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />{e}</li>)}
        </ul>
      )}
      {offer.launch_gate_note && (
        <p className="text-xs text-foreground/70 flex gap-1.5 mb-2"><XCircle className="w-3 h-3 shrink-0 mt-0.5" />{offer.launch_gate_note}</p>
      )}
      <p className="text-xs flex items-center gap-1.5 text-foreground/70" data-testid={`billing-offer-checkout-enabled-${offer.offer_key}`}>
        {offer.checkout_enabled ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
        Checkout is {offer.checkout_enabled ? "live" : "disabled"} for this offer
      </p>
      <div className="mt-3">
        {offer.checkout_enabled ? (
          <button
            data-testid={`billing-offer-disable-checkout-btn-${offer.offer_key}`}
            onClick={() => setGate(false)}
            disabled={busy}
            className="text-xs uppercase tracking-wide border border-foreground/40 px-3 py-1.5 hover:border-foreground disabled:opacity-40"
          >
            Turn Checkout Off
          </button>
        ) : confirmingEnable ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-foreground/70">Allow real customers to check out for this offer?</span>
            <button data-testid={`billing-offer-confirm-enable-checkout-btn-${offer.offer_key}`} onClick={() => setGate(true)} disabled={busy}
              className="text-xs uppercase tracking-wide bg-foreground text-background px-3 py-1.5 disabled:opacity-40">
              Yes, Turn It On
            </button>
            <button onClick={() => setConfirmingEnable(false)} disabled={busy}
              className="text-xs uppercase tracking-wide text-foreground/60 hover:text-foreground px-2 py-1.5">
              Cancel
            </button>
          </div>
        ) : (
          <button
            data-testid={`billing-offer-enable-checkout-btn-${offer.offer_key}`}
            onClick={() => setConfirmingEnable(true)}
            disabled={busy}
            className="text-xs uppercase tracking-wide border border-foreground/40 px-3 py-1.5 hover:border-foreground disabled:opacity-40"
          >
            Turn Checkout On
          </button>
        )}
      </div>
    </div>
  );
}

function WebhookCard({ data }) {
  const copy = () => {
    navigator.clipboard.writeText(data?.endpoint_url || "");
    toast.success("Webhook URL copied");
  };
  return (
    <div className="bg-card border border-border p-6 mb-8" data-testid="billing-webhook-card">
      <h2 className="text-lg font-subtitle mb-3">Webhook Endpoint</h2>
      <div className="flex items-center gap-2 mb-3">
        <code data-testid="billing-webhook-url" className="bg-background border border-border px-3 py-2 text-xs flex-1 overflow-x-auto">{data?.endpoint_url}</code>
        <button data-testid="billing-webhook-copy-btn" onClick={copy} className="text-foreground/75 hover:text-foreground p-2"><Copy className="w-4 h-4" /></button>
      </div>
      <p className="text-xs text-foreground/70 mb-2">Required events:</p>
      <div className="flex flex-wrap gap-2 mb-3">
        {data?.required_events?.map((e) => (
          <span key={e} className="text-xs border border-border px-2 py-1 font-mono text-foreground/70">{e}</span>
        ))}
      </div>
      <StatusBadge status={data?.signing_secret_configured ? "verified" : "missing"}
        extra={data?.signing_secret_configured ? "configured" : "not configured"} testId="billing-webhook-secret-status" />
      <p className="text-xs text-foreground/75 mt-2">{data?.signature_verification}</p>
      <p className="text-xs text-foreground/60 mt-2">{data?.note}</p>
    </div>
  );
}

export default function AdminBilling() {
  const api = useApi();
  const qc = useQueryClient();
  const [validating, setValidating] = useState(false);

  const { data: connection } = useQuery({ queryKey: ["billing-connection"], queryFn: async () => (await api.get("/admin/billing/connection")).data });
  const { data: offers } = useQuery({ queryKey: ["billing-offers"], queryFn: async () => (await api.get("/admin/billing/offers")).data });
  const { data: webhook } = useQuery({ queryKey: ["billing-webhook"], queryFn: async () => (await api.get("/admin/billing/webhook")).data });

  const revalidateConnection = async () => {
    setValidating(true);
    try {
      await api.post("/admin/billing/connection/validate");
      qc.invalidateQueries({ queryKey: ["billing-connection"] });
    } finally {
      setValidating(false);
    }
  };

  const saveOffer = async (offerKey, priceId) => {
    const res = await api.patch(`/admin/billing/offers/${offerKey}`, { stripe_price_id: priceId });
    qc.invalidateQueries({ queryKey: ["billing-offers"] });
    return res.data;
  };

  const validateOffer = async (offerKey) => {
    await api.post(`/admin/billing/offers/${offerKey}/validate`);
    qc.invalidateQueries({ queryKey: ["billing-offers"] });
  };

  const setOfferGate = async (offerKey, enabled) => {
    await api.post(`/admin/billing/offers/${offerKey}/gate`, { enabled });
    qc.invalidateQueries({ queryKey: ["billing-offers"] });
  };

  return (
    <div data-testid="admin-billing-page">
      <h1 className="font-display text-display-xs mb-6">Billing &amp; Stripe Configuration</h1>
      <ConnectionCard data={connection} onValidate={revalidateConnection} validating={validating} />
      <h2 className="text-sm uppercase tracking-widest text-foreground/70 mb-4 font-subtitle">Offer → Stripe Price Mapping</h2>
      {offers?.map((o) => (
        <OfferRow key={o.offer_key} offer={o} onSave={saveOffer} onValidate={validateOffer} onSetGate={setOfferGate} />
      ))}
      <WebhookCard data={webhook} />
    </div>
  );
}
